import React, { Fragment } from 'react'
import thumbnailStyles from './Thumbnail.module.scss'
import Icon, { IconType } from '../Icon'
import classnames from 'classnames'
import { ThumbnailProps, ThumbnailState } from './types'
import AspectRatio from 'react-aspect-ratio'
import 'react-aspect-ratio/aspect-ratio.css'
import get from 'lodash/get'
import Tooltip from 'react-tooltip'

const ProcessingOverlay = ({processingOverlayStyles} : {processingOverlayStyles?: any}) => (
  <div data-bdd='processing-overlay' style={processingOverlayStyles}
  className={classnames({
    [thumbnailStyles['thumbnail-processing-overlay']]: true
  })}>
    <Icon className={thumbnailStyles['spinner-icon']} name={IconType.iconName.spinner} />
  </div>
)

const FailedOverlay = ({extraBackground, failedOverlayStyles} : {extraBackground?: boolean, failedOverlayStyles?: any}) => (
  <div data-bdd='failed-overlay' style={failedOverlayStyles} className={classnames({
    [thumbnailStyles['thumbnail-failed-overlay']]: true,
    [thumbnailStyles['thumbnail-failed-overlay-extra-bg']]: extraBackground
  })}>
    <Icon name={ IconType.iconName.danger } className={thumbnailStyles['alert-icon']} colour={IconType.colour.white} />
    Image failed to process. Please try again.
  </div>
)

class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {

  constructor (props) {

    super(props)

    this.state = {
      imgLoadError: props.imgLoadError || null,
      aspectRatio: '16/9'
    }

  }

  setImgLoadError (error = false) {

    this.setState({ imgLoadError: error })

  }

  componentDidUpdate(prevProps) {

    if(this.props.src !== prevProps.src && this.state.imgLoadError) {

      this.setImgLoadError(false)

    }

  }

  handleLoad = (ev: React.SyntheticEvent<HTMLImageElement, Event>) => {

    this.setImgLoadError(false)

    if (!this.props.onLoadAspectRatio) return

    let width
    let height

    width = get(ev.target, 'naturalWidth')
    height = get(ev.target, 'naturalHeight')

    if (width === 0 || height === 0) {
      
      /*
        If we can't get naturalWidth/Height for some reason, use standard width and height.
        naturalWidth/Height refers to the native image size whereas width and height can be affected by CSS.
      */
      width = get(ev.target, 'width', 0)
      height = get(ev.target, 'height', 0)

      if (width === 0 || height === 0) return

    }

    const ratioCalculation = height / width
    const aspectRatio = ratioCalculation === 0.75 ? '4/3' : '16/9'

    this.setState({ aspectRatio })
    this.props.onLoadAspectRatio(aspectRatio)

  }

  getTooltipProps = () => {
    let props = {}
    
    if (this.props.isProcessing) {
      props['data-for'] = 'main'
      props['data-tip'] = 'Your image is processing'
      props['data-class'] = thumbnailStyles['tooltip-class']
    }

    return props
  }

  ConditionalWrapper = ({ condition, children, thumbnailClasses, onClick }) => 
    condition
      ? (<AspectRatio
          ratio={this.props.ratio}
          data-bdd={ this.props.bdd || 'thumbnail-container'}
          onClick={onClick}
          className={classnames(thumbnailClasses, this.props.missingThumbnailClassName ?? '')}
          {...this.getTooltipProps()}
        >{ children }</AspectRatio>)
      : (<div
          data-bdd={ this.props.bdd || 'thumbnail-container'}
          className={thumbnailClasses}
          onClick={onClick}
          {...this.getTooltipProps()}
        >{ children }</div>)

  render () {

    const { imgLoadError = false } = this.state

    const thumbnailClasses = classnames(
      thumbnailStyles['thumbnail'], {
        [thumbnailStyles['thumbnail--borderless']]: this.props.borderless,
        [thumbnailStyles['thumbnail--small']]: this.props.smallFallbackImage ?? false,
        [thumbnailStyles['thumbnail--43']] : this.state.aspectRatio === '4/3',
        [thumbnailStyles['thumbnail--bg-transparent']]: this.props.transparentBg
      },
      'thumbnail',
      this.props.className
    )

    return (
      <this.ConditionalWrapper
        condition={this.props.ratio}
        thumbnailClasses={thumbnailClasses}
        onClick={(e) => {
          if (this.props.onClick)
            this.props.onClick(e)
        }}
      >
        {this.props.isProcessing && <ProcessingOverlay processingOverlayStyles={this.props.processingOverlayStyles}/>}
        {this.props.hasFailed && <FailedOverlay extraBackground={!!this.props.src} failedOverlayStyles={this.props.failedOverlayStyles}/>}
        {!this.props.isProcessing && !imgLoadError && this.props.src && <img
          src={this.props.src}
          alt={this.props.alt}
          onError={() => this.setImgLoadError(true)}
          onLoad={this.handleLoad}
          style={this.props.imgStyle}
          className={this.props.imgClassName || thumbnailStyles['thumbnail-img']}
          data-bdd={this.props.imgBdd}
          width={this.props.width}
          height={this.props.height}
        />}
        {this.props.customOverlay && <this.props.customOverlay />}
        {this.props.isProcessing &&
        <Tooltip
          id='main'
          place='top'
          type='dark'
          wrapper='span'
          effect='float'
        />
        }
      </this.ConditionalWrapper>
    )
  }

}

export default Thumbnail
